import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, put } from "../../lib/api";
import {
  HEARTBEAT_MIN_S,
  type HeartbeatPushOutcome,
  heartbeatError,
  heartbeatPushOutcome,
} from "../../lib/station";
import type { ChargePoint } from "../../lib/types";
import { Button } from "../ui/Button";
import { Notice } from "../ui/Feedback";
import { Field } from "../ui/Input";
import { SettingRow } from "../ui/SettingRow";

/**
 * How often a station checks in, and the edit for it.
 *
 * Saving writes the station's row, which is what its BootNotification is answered
 * with, and pushes the value at once if the station is connected. The result line
 * says which of those happened — "saved and in use", "saved, offline, used at next
 * boot", or the station's own refusal — because "Saved" alone would let an operator
 * believe a station is on 60 s when its firmware said no.
 *
 * Shorter is not free: every heartbeat is a frame in the message log and a row
 * update. Below a minute is for watching a station you are commissioning, not for
 * a fleet.
 */

interface HeartbeatResult {
  readonly chargePoint: ChargePoint;
  readonly push: { readonly status: string };
}

export function HeartbeatInterval({
  station,
  canEdit,
}: {
  readonly station: ChargePoint;
  readonly canEdit: boolean;
}) {
  const client = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(String(station.heartbeatIntervalS));
  const [outcome, setOutcome] = useState<HeartbeatPushOutcome | undefined>();
  const [failed, setFailed] = useState<string | undefined>();

  const save = useMutation({
    mutationFn: (heartbeatIntervalS: number) =>
      put<HeartbeatResult>(`/charge-points/${encodeURIComponent(station.id)}/heartbeat-interval`, {
        heartbeatIntervalS,
      }),
    onSuccess: (result) => {
      setIsEditing(false);
      setFailed(undefined);
      setDraft(String(result.chargePoint.heartbeatIntervalS));
      setOutcome(heartbeatPushOutcome(result.push.status, result.chargePoint.heartbeatIntervalS));
      // The masthead's "every Ns" and the frame log both change with this.
      void client.invalidateQueries({ queryKey: ["charge-point", station.id] });
      void client.invalidateQueries({ queryKey: ["charge-points"] });
    },
    onError: (error: unknown) => {
      setOutcome(undefined);
      setFailed(
        error instanceof ApiError ? error.message : "The interval could not be saved. Try again.",
      );
    },
  });

  const issue = draft === "" ? "Enter a number of seconds." : heartbeatError(draft);
  const unchanged = Number(draft) === station.heartbeatIntervalS;

  return (
    <SettingRow
      label="Heartbeat interval"
      value={`Every ${station.heartbeatIntervalS}s`}
      hint="A station that misses its heartbeats is how the panel learns it has gone quiet."
      actions={
        !canEdit || isEditing ? null : (
          <Button
            onClick={() => {
              // From the row as it is now: another operator, or the poll, may have
              // changed it since this panel last saved.
              setDraft(String(station.heartbeatIntervalS));
              setOutcome(undefined);
              setIsEditing(true);
            }}
          >
            Change interval
          </Button>
        )
      }
    >
      {failed === undefined ? null : <Notice tone="error">{failed}</Notice>}
      {outcome === undefined ? null : <Notice tone={outcome.tone}>{outcome.text}</Notice>}

      {isEditing ? (
        <>
          <Field
            label="Heartbeat interval (s)"
            type="number"
            inputMode="numeric"
            step={1}
            min={HEARTBEAT_MIN_S}
            value={draft}
            disabled={save.isPending}
            onChange={(event) => setDraft(event.target.value)}
            hint={
              station.connected
                ? "Pushed to the station as soon as you save."
                : "The station is offline. It is told the new interval when it next boots."
            }
            {...(issue === undefined ? {} : { error: issue })}
          />

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              pending={save.isPending}
              disabled={issue !== undefined || unchanged}
              onClick={() => {
                setFailed(undefined);
                setOutcome(undefined);
                save.mutate(Number(draft));
              }}
            >
              Save interval
            </Button>
            <Button
              disabled={save.isPending}
              onClick={() => {
                setIsEditing(false);
                setFailed(undefined);
                setDraft(String(station.heartbeatIntervalS));
              }}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : null}
    </SettingRow>
  );
}
